import { getOrdersHistoryV31 } from '@/api/orders';
import { useInfiniteQuery } from '@tanstack/react-query';
import EditOrderAside from '@/components/EditOrder/EditOrderAside.jsx';
import { DeliveryServicesContext } from '@/contexts/DeliveryServicesContext.jsx';
import useOrderEditModes from '@/hooks/useOrders.js';
import { ResponsiveMenuStore } from '@/store/sideSheet';
import { OrderFromHistory } from '@/store/order';
import { useRouter } from 'next/router';
import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { createOrderStatusMap, formatOrder, createOrderDetails } from '@/utils/orders';
import EmptyState from '../../../public/assets/wave-grocery/Empty-States-Default/Empty-Cart.png';
import { OrderHistoryList } from '@desquared/wave-components-library';
import Overlay from '../Overlay/Overlay';
import { updateConfiguration } from '@/utils/getConfiguration';
import ConfigurationStore from '@/store/configuration';
import LoaderStore from '@/store/loader';

import styles from '@/styles/User.module.css';
import { EditModeCart, isEditStarted, productsToKeep } from '@/store/cart';
import { useTranslations } from 'next-intl';
import { cancelOrderEdit } from '@/api/wave/orders';
import { ModalContext } from '@/contexts/ModalProvider';
import { CurrencyContext } from '@/contexts/CurrencyProvider';
import { useDateFormatting } from '@/contexts/DateFormattingProvider';
import { useConfiguration } from '@/contexts/ConfigContext';

let TIMER = undefined;
const SCROLL_THRESHOLD = 400;

export default function UserOrders({ user, image, categoriesFromFile }) {
  const router = useRouter();
  const { timeFormat } = useDateFormatting();
  const [orders, setOrders] = useState([]);
  const [orderStatusMap, setOrderStatusMap] = useState();
  const t = useTranslations('user_history');
  const common = useTranslations('common');
  const [selectedOrder, setSelectedOrder] = useState();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const listInnerRef = useRef();
  const { token } = user;
  const { deliveryServices } = useContext(DeliveryServicesContext);
  const { formatter } = useContext(CurrencyContext);
  const [orderDetails, setOrderDetails] = useState([]);
  const [formattedOrders, setFormattedOrders] = useState([]);
  const { openActionModal, close } = useContext(ModalContext);
  const { orderEditModes } = useOrderEditModes(selectedOrder?._id);
  const { config } = useConfiguration();

  function selectOrder(_order) {
    OrderFromHistory.update(_order);
  }

  useEffect(() => {
    const sub = ConfigurationStore.subscribe(config => {
      if (config) setOrderStatusMap(createOrderStatusMap(config));
    });

    return () => {
      sub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    updateConfiguration();
  }, [router.locale]);

  const openMenu = () => {
    ResponsiveMenuStore.open();
  };

  const handleSelectOrderForEdit = id => {
    setSelectedOrder(orders?.find(i => i._id === id));
    setIsEditOpen(true);
  };

  const handleUpdateOrder = updatedOrder => {
    const updatedOrders = orders.map(order => (order._id === updatedOrder._id ? { ...updatedOrder } : order));
    setOrders(updatedOrders);
  };

  const fetchOrders = async ({ pageParam }) => {
    const results = await getOrdersHistoryV31({ pageNumber: pageParam });

    return results;
  };

  const {
    data: ordersResponse,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['orders', router.locale],
    queryFn: fetchOrders,
    enabled: !!token,
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.pageNumber + 1 >= lastPage.totalPages) return undefined;

      return lastPage.pageNumber + 1;
    },
    initialPageParam: 0,
    staleTime: 0,
  });

  useEffect(() => {
    if (!ordersResponse || !orderStatusMap) return;
    const allOrders = ordersResponse.pages.flatMap(page => page.data);
    const formattedOrders = allOrders.map(order => {
      return {
        ...formatOrder(
          {
            ...order,
          },
          orderStatusMap,
          router.locale,
          timeFormat,
          config?.format?.addressStreetPattern
        ),
        showIcon: true,
        handleClick: () => {
          selectOrder(allOrders.find(i => i._id === order._id));
          router.push(`/user/history/${order._id}/details`);
        },
      };
    });

    setOrders(allOrders);
    setFormattedOrders(formattedOrders);
    setOrderDetails(
      allOrders.length
        ? createOrderDetails(
            { data: allOrders },
            deliveryServices,
            {
              paymentMethodToken: t('badges.payment_method'),
              productsToken: t('badges.products'),
            },
            [
              common('days.sunday'),
              common('days.monday'),
              common('days.tuesday'),
              common('days.wednesday'),
              common('days.thursday'),
              common('days.friday'),
              common('days.saturday'),
            ],
            router.locale,
            t('predictive_time_title'),
            formatter,
            timeFormat,
            config?.format?.addressStreetPattern
          )
        : []
    );
  }, [
    ordersResponse,
    setOrders,
    router,
    orderStatusMap,
    deliveryServices,
    setFormattedOrders,
    router.locale,
    t,
    common,
  ]);

  const observer = useRef(null);
  const lastResultItemRef = useCallback(
    node => {
      if (!node) return;
      if (isLoading || isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      });

      observer.current.observe(node);
    },
    [isLoading, isFetchingNextPage, fetchNextPage]
  );

  useEffect(() => {
    LoaderStore.setIsVisible(isLoading || isFetchingNextPage);
  }, [isLoading, isFetchingNextPage]);

  if (!user) return null;
  return (
    <div ref={listInnerRef} className={styles.ordersScrollContainer}>
      <OrderHistoryList
        items={formattedOrders}
        title={t('title')}
        emptyOrderHistoryWording={t('empty')}
        loginWording={!user ? t('login') : undefined}
        emptyOrderHistoryListMessageOne={t('message_one')}
        emptyOrderHistoryListMessageTwo={t('message_two')}
        emptyOrderHistoryImg={image ? image : EmptyState.src}
        onBack={() => {
          openMenu();
        }}
        onEdit={handleSelectOrderForEdit}
        orderDetails={orderDetails}
        lastItemRef={lastResultItemRef}
        editText={t('edit.can_edit')}
        editButtonText={t('edit.cta')}
        orderShortHandTitle={t('order_shorthand')}
      />
      {selectedOrder && (
        <>
          <Overlay
            isVisible={isEditOpen}
            onClick={() => {
              setIsEditOpen(false);
              isEditStarted.update(false);

              productsToKeep.release();
              setSelectedOrder(undefined);
              cancelOrderEdit(selectedOrder?._id, EditModeCart.getValue());
              openActionModal({
                closeOnOuterClick: true,
                title: `${common('asides.side_cart.cancel_back_edit')}`,
                text: common('asides.side_cart.cancel_back_edit_description'),
                actions: [
                  {
                    btnLabel: common('asides.side_cart.btn_label'),
                    onClick: () => {
                      close();
                    },
                  },
                ],
                img: {
                  url: '/assets/wave-grocery/Bus.png',
                  ariaLabel: common('asides.side_cart.cancel_back_edit'),
                  size: 'medium',
                },
              });
            }}
          />
          <EditOrderAside
            isOpen={isEditOpen}
            onClose={() => {
              setIsEditOpen(false);
              setSelectedOrder(undefined);
            }}
            editModes={orderEditModes}
            selectedOrder={selectedOrder}
            onUpdateOrder={handleUpdateOrder}
            categoriesFromFile={categoriesFromFile}
          />
        </>
      )}
    </div>
  );
}
